import React, { useEffect } from 'react';
import Panel from '../../ui/Panel/Panel';
import { LayoutTree } from '../DynamicLayout';
import {
  SmallHeader as BlockHeader,
  MediumHeader as ContentTitle,
} from '../../ui/Header/Header';
import TextLink from '../../ui/TextLink/TextLink';
import Paragraph from '../../ui/Paragraph/Paragraph';
import { useTranslations } from '../../../hooks/useTranslations';
import { useSettings } from '../../../hooks/useSettings';
import {
  useAnalyticsPost,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import { useTheme } from '../../../hooks/useTheme';
import styles from './MoreInformation.module.css';

const { FeatureLayout } = LayoutTree;

export interface MoreInfoProps {
  featureName?: string;
  fixedPanelHeight?: string;
  layout?: LayoutTree.FeatureLayout;
}

const MoreInformation = ({
  featureName,
  fixedPanelHeight,
  layout = FeatureLayout.PORTRAIT,
}: MoreInfoProps) => {
  const {
    state: { trackingStatus },
  } = usePageContext();

  const {
    linksNoneDefault: linkTextDefaultStyle,
    linksNoneHover: linkTextHoverStyle,
    panelcontainerNoneNone,
  } = useTheme();

  const { borderTopColor, borderTopStyle, borderTopWidth } =
    panelcontainerNoneNone ?? {}; // Can be undefined or null;

  const {
    moreInformationHeader,
    moreInformationTitle,
    moreInformationText,
    moreInformationLink,
  } = useTranslations();

  const { moreInformation } = useSettings();

  const {
    showBlockHeader: showBlockHeaderRaw,
    showLink: showLinkRaw,
    link,
  } = moreInformation ?? {};

  const showBlockHeader =
    typeof showBlockHeaderRaw === 'boolean' ? showBlockHeaderRaw : true;
  const showLink = typeof showLinkRaw === 'boolean' ? showLinkRaw : true;

  const isLandscape = layout === FeatureLayout.LANDSCAPE;
  const logEvent = useAnalyticsPost();

  useEffect(() => {
    logEvent({
      eventComponent: NarvarEventComponents.NTH_PACK_MORE_INFORMATION,
      eventFeature: NarvarEventComponents.NTH_PACK_MORE_INFORMATION,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_PACK_MORE_INFORMATION,
      eventType: NarvarEventTypes.COMPONENT_RENDER,
      trackLayout: layout,
    });
  }, [layout, logEvent, featureName]);

  const logMoreInfoLinkClick = () => {
    logEvent({
      eventComponent: NarvarEventComponents.NTH_PACK_MORE_INFORMATION,
      eventFeature: NarvarEventComponents.NTH_PACK_MORE_INFORMATION,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_PACK_MORE_INFORMATION,
      clickCategory: NarvarClickSubTypes.EXTERNAL_LINK,
      clickLinkDestinationUrl: link,
      clickLinkLabel: moreInformationLink,
      clickSubType: NarvarClickSubTypes.MORE_INFORMATION_LINK,
      clickType: NarvarClickTypes.OUTGOING,
      eventType: NarvarEventTypes.CLICK,
    });
  };

  const homePickupStatus = [
    'HPUSCHEDULED',
    'HPUPICKUPINPROGRESS',
    'HPUPICKEDUP',
    'HPUEXCEPTION',
  ];
  const homePickupLink = 'https://corp.narvar.com/home-pickup-consumer';
  const isHomePickup = homePickupStatus.includes(trackingStatus ?? '');
  const infoLink = isHomePickup && !link ? homePickupLink : link;
  const hasHeader = showBlockHeader && moreInformationHeader;

  return (
    <Panel header="" fixedPanelHeight={fixedPanelHeight}>
      {isLandscape ? (
        <div className={styles.landscapeContainer}>
          {hasHeader && (
            <div
              className={styles.landscapeHeader}
              style={{
                borderRight: `
                  ${borderTopWidth ?? '1px'} 
                  ${borderTopStyle ?? 'solid'} 
                  ${borderTopColor ?? '#CCCCCC'}
                `,
              }}
            >
              <BlockHeader as="h2">{moreInformationHeader}</BlockHeader>
            </div>
          )}
          <div
            className={styles.landscapeMain}
            style={{
              gridColumn: `span ${hasHeader ? 9 : 10}`,
              paddingLeft: `${hasHeader ? '1.5rem' : 0}`,
            }}
          >
            <div>
              <ContentTitle as={hasHeader ? 'p' : 'h2'}>
                {moreInformationTitle}
              </ContentTitle>
              {moreInformationText && (
                <Paragraph containerCustomStyle={{ margin: '0.75rem 0 0' }}>
                  {moreInformationText}
                </Paragraph>
              )}
            </div>
          </div>
          {showLink && moreInformationLink && infoLink && (
            <div className={styles.landscapeLink}>
              <TextLink
                href={infoLink}
                linkStyle={{
                  sDefault: {
                    ...linkTextDefaultStyle,
                    textAlign: 'right',
                  },
                  sHover: {
                    ...linkTextHoverStyle,
                    textAlign: 'right',
                  },
                }}
                onClick={logMoreInfoLinkClick}
              >
                {moreInformationLink}
              </TextLink>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.portraitContainer}>
          {showBlockHeader && (
            <BlockHeader
              as="h2"
              containerCustomStyle={{
                marginBottom: '1rem',
              }}
            >
              {moreInformationHeader}
            </BlockHeader>
          )}
          <ContentTitle as={showBlockHeader ? 'p' : 'h2'}>
            {moreInformationTitle}
          </ContentTitle>
          {moreInformationText && (
            <Paragraph containerCustomStyle={{ margin: '0.75rem 0' }}>
              {moreInformationText}
            </Paragraph>
          )}
          {showLink && moreInformationLink && infoLink && (
            <TextLink
              href={infoLink}
              linkStyle={{
                sDefault: linkTextDefaultStyle,
                sHover: linkTextHoverStyle,
              }}
              onClick={logMoreInfoLinkClick}
            >
              {moreInformationLink}
            </TextLink>
          )}
        </div>
      )}
    </Panel>
  );
};

export default MoreInformation;
